import { type ReactNode, useEffect } from 'react'
import { useShellStore, type ShellThemeMode } from '@/store/shell'

/** Browser chrome colour = the page background token of the active theme. */
function readThemeColor() {
  const background = getComputedStyle(document.documentElement)
    .getPropertyValue('--background')
    .trim()
  return background ? `hsl(${background})` : null
}

function resolveThemeMode(themeMode: ShellThemeMode) {
  if (themeMode !== 'system') {
    return themeMode
  }

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyResolvedTheme(resolvedTheme: 'light' | 'dark') {
  const root = document.documentElement
  root.classList.toggle('dark', resolvedTheme === 'dark')
  root.style.colorScheme = resolvedTheme
  root.setAttribute('data-theme', resolvedTheme)

  const themeColorMeta = document.querySelector('meta[name="theme-color"]')
  const themeColor = readThemeColor()
  if (themeColorMeta && themeColor) {
    themeColorMeta.setAttribute('content', themeColor)
  }
}

interface ThemeProviderProps {
  children: ReactNode
}

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const themeMode = useShellStore((state) => state.themeMode)

  useEffect(() => {
    const syncTheme = () => {
      applyResolvedTheme(resolveThemeMode(themeMode))
    }

    syncTheme()

    if (themeMode !== 'system' || typeof window.matchMedia !== 'function') {
      return
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', syncTheme)
    return () => media.removeEventListener('change', syncTheme)
  }, [themeMode])

  return <>{children}</>
}
